import React, { useState, useEffect, useContext } from "react";
import axios from "axios";
import Table from "../components/Table/Table";
import SearchBar from "../components/SearchBar/SearchBar";
import Button from "../components/Button/Button";
import "../pagesStyling/DataCollection.css";
import googleLogo from "../resources/images/icons/google_logo.svg";
import jiraLogo from "../resources/images/icons/jira_logo.svg";
import confluenceLogo from "../resources/images/icons/confluence_logo.svg";
import windchillLogo from "../resources/images/icons/windchill_logo.svg";
import { Link, useNavigate } from "react-router-dom";
import { AuthContext } from "../components/AuthContext/AuthContext";
import { useSelectedCollections } from "../components/SelectedCollectionsContext/SelectedCollectionsContext";
import deleteIcon from "../resources/images/icons/bin.svg";
import editIcon from "../resources/images/icons/settings.svg";
import checkmark from "../resources/images/icons/checkmark.svg";
import { API_BASE_URL } from "../apiConfig";

function DataCollection() {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedRows, setSelectedRows] = useState<
    Record<string, React.ReactNode>[]
  >([]);
  const [tableData, setTableData] = useState<Record<string, React.ReactNode>[]>(
    []
  );
  const [selectedUuids, setSelectedUuids] = useState<string[]>([]);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [isEditPopupOpen, setIsEditPopupOpen] = useState(false);
  const [editCollection, setEditCollection] = useState<{
    uuid: string;
    name: string;
    public: boolean;
    restricted: boolean;
  } | null>(null);
  const { token } = useContext(AuthContext) || {};
  const { setSelectedCollections, selectedCollections } =
    useSelectedCollections();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/collections`, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          withCredentials: true,
        });

        const result = response.data;
        const formattedData = result.collections.map((collection: any) => ({
          Name: collection.name,
          Origin: (
            <a
              href={collection.source_url}
              target="_blank"
              rel="noopener noreferrer"
            >
              <img
                src={getOriginIcon(collection.source_origin)}
                alt={collection.source_origin}
              />
            </a>
          ),
          "Source ID": collection.parent_id,
          "Last synced at": collection.last_sync_at
            ? new Date(collection.last_sync_at).toLocaleString("en-US", {
                month: "short",
                day: "numeric",
                year: "numeric",
                hour: "numeric",
                minute: "2-digit",
                hour12: true,
              })
            : "Never",
          Owner: collection.created_by,
          Public: collection.public_collection ? (
            <img src={checkmark}></img>
          ) : (
            "❌"
          ),
          Actions: (
            <div className="actions-cell">
              {collection.has_write_access && (
                <>
                  <button
                    className="edit-btn"
                    onClick={() =>
                      handleEditClick({
                        uuid: collection.uuid,
                        name: collection.name,
                        public: collection.public_collection,
                        restricted: collection.restricted,
                      })
                    }
                  >
                    <img src={editIcon}></img>
                  </button>
                  <button
                    className="delete-btn"
                    onClick={() => handleDeleteClick(collection.uuid)}
                  >
                    <img src={deleteIcon}></img>
                  </button>
                </>
              )}
            </div>
          ),
          uuid: collection.uuid,
          public: collection.public_collection,
          restricted: collection.restricted,
          has_read_access: !!collection.has_read_access,
          has_write_access: !!collection.has_write_access,
        }));
        setTableData(formattedData);

        const selectedUuidsFromContext = selectedCollections.map(
          (col) => col.uuid
        );
        const preSelectedRows = formattedData.filter((row: { uuid: string }) =>
          selectedUuidsFromContext.includes(row.uuid as string)
        );
        setSelectedRows(preSelectedRows);
        setSelectedUuids(selectedUuidsFromContext);
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    if (token) {
      fetchData();
    }
  }, [token, selectedCollections]);

  const getOriginIcon = (sourceOrigin: string) => {
    switch (sourceOrigin) {
      case "GDRIVE":
        return googleLogo;
      case "JIRA":
        return jiraLogo;
      case "CONFLUENCE":
        return confluenceLogo;
      case "WINDCHILL":
        return windchillLogo;
      default:
        return googleLogo;
    }
  };

  const headers = [
    "Name",
    "Origin",
    "Last synced at",
    "Owner",
    "Public",
    "Actions",
  ];

  const filteredData = tableData.filter((row) =>
    Object.values(row).some((value) =>
      typeof value === "string"
        ? value.toLowerCase().includes(searchTerm.toLowerCase())
        : false
    )
  );

  const handleSearch = (value: string) => {
    setSearchTerm(value);
  };

  const handleRowSelection = (selected: Record<string, React.ReactNode>[]) => {
    const validSelected = selected.filter((row) => row.has_read_access);
    setSelectedRows(validSelected);
    const uuids = validSelected.map((row) => row.uuid as string);
    setSelectedUuids(uuids);
    setSelectedCollections(
      validSelected.map((row) => ({
        uuid: row.uuid as string,
        name: row.Name as string,
      }))
    );
  };

  const handleUseSelectedCollections = async () => {
    if (selectedRows.length === 0) {
      setSuccessMessage("No data collections selected.");
      setTimeout(() => setSuccessMessage(null), 3000);
      return;
    }

    try {
      const selectedCollectionsForContext = selectedRows.map((row) => ({
        uuid: row.uuid as string,
        name: row.Name as string,
      }));
      setSelectedCollections(selectedCollectionsForContext);
      const collections = selectedCollectionsForContext.map(
        (collection) => collection.uuid
      );
      const response = await axios.post(
        `${API_BASE_URL}/chat/new`,
        { collections },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          withCredentials: true,
        }
      );

      const newChatId = response.data.chat_uuid;

      setSuccessMessage("Data collections saved and new chat created!");
      setTimeout(() => setSuccessMessage(null), 3000);

      navigate(`/chat/${newChatId}`);
    } catch (error) {
      console.error("Error creating new chat:", error);
      setSuccessMessage("Failed to create new chat. Please try again.");
      setTimeout(() => setSuccessMessage(null), 3000);
    }
  };

  const handleEditClick = (collection: {
    uuid: string;
    name: string;
    public: boolean;
    restricted: boolean;
  }) => {
    setEditCollection(collection);
    setIsEditPopupOpen(true);
  };

  const handleEditSave = async () => {
    if (!editCollection || !token) return;

    try {
      const response = await axios.put(
        `${API_BASE_URL}/collections/`,
        {
          uuid: editCollection.uuid,
          name: editCollection.name,
          public: editCollection.public,
          restricted: editCollection.restricted,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          withCredentials: true,
        }
      );

      setTableData((prevData) =>
        prevData.map((row) =>
          row.uuid === editCollection.uuid
            ? {
                ...row,
                Name: editCollection.name,
                Public: editCollection.public ? (
                  <img src={checkmark}></img>
                ) : (
                  "❌"
                ),
                public: editCollection.public,
                restricted: editCollection.restricted,
              }
            : row
        )
      );

      setSelectedCollections((prev) =>
        prev.map((col) =>
          col.uuid === editCollection.uuid
            ? { ...col, name: editCollection.name }
            : col
        )
      );

      setSuccessMessage("Data collection updated successfully");
      setTimeout(() => setSuccessMessage(null), 3000);
      setIsEditPopupOpen(false);
    } catch (error: any) {
      console.error("Error updating data collection:", error);
      const errorMessage =
        error.response?.data?.message || "Failed to update data collection";
      setSuccessMessage(errorMessage);
      setTimeout(() => setSuccessMessage(null), 3000);
    }
  };

  const handleEditCancel = () => {
    setIsEditPopupOpen(false);
    setEditCollection(null);
  };

  const handleDeleteClick = async (uuid: string) => {
    if (!token) return;

    try {
      await axios.delete(`${API_BASE_URL}/collections/single?uuid=${uuid}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        withCredentials: true,
      });

      setTableData((prevData) => prevData.filter((row) => row.uuid !== uuid));

      setSelectedRows((prevSelected) =>
        prevSelected.filter((row) => row.uuid !== uuid)
      );
      setSelectedUuids((prevUuids) => prevUuids.filter((id) => id !== uuid));

      setSelectedCollections((prev) => prev.filter((col) => col.uuid !== uuid));

      setSuccessMessage("Data collection deleted successfully");
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (error: any) {
      console.error("Error deleting data collection:", error);
      const errorMessage =
        error.response?.data?.message || "Failed to delete data collection";
      setSuccessMessage(errorMessage);
      setTimeout(() => setSuccessMessage(null), 3000);
    }
  };

  return (
    <div className="data-collections-page">
      <div className="table-header">
        <h1>Data collections</h1>
        <div className="actions">
          <SearchBar
            placeholder="Search data collections"
            onSearch={handleSearch}
          />
          <Link to="/add-data-collection">
            <Button text="+ Add data collection" />
          </Link>
        </div>
      </div>

      <Table
        headers={headers}
        data={filteredData}
        onSelectRows={handleRowSelection}
        isRowSelectable={(row: Record<string, React.ReactNode>): boolean =>
          !!row.has_read_access
        }
        preSelectedRows={selectedRows}
      />

      <div className="data-collection-use-button">
        <Button
          text="Use selected data collections"
          onClick={handleUseSelectedCollections}
          disabled={selectedRows.length === 0}
        />
      </div>
      {successMessage && (
        <div className="success-message">{successMessage}</div>
      )}

      {isEditPopupOpen && editCollection && (
        <div className="edit-popup-overlay">
          <div className="edit-popup">
            <h2>Edit Data Collection</h2>
            <div className="edit-form">
              <label>
                Name:
                <input
                  type="text"
                  value={editCollection.name}
                  onChange={(e) =>
                    setEditCollection({
                      ...editCollection,
                      name: e.target.value,
                    })
                  }
                />
              </label>
              <label>
                Visibility:
                <select
                  value={editCollection.public ? "public" : "private"}
                  onChange={(e) =>
                    setEditCollection({
                      ...editCollection,
                      public: e.target.value === "public",
                    })
                  }
                >
                  <option value="public">Public</option>
                  <option value="private">Private</option>
                </select>
              </label>
            </div>
            <div className="edit-popup-buttons">
              <Button text="Save" onClick={handleEditSave} />
              <Button text="Cancel" onClick={handleEditCancel} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default DataCollection;
